// src/modules/app-views/products/pages/Document/tabs/StockTab/StockCard.jsx
// *******************************************************************************
// STOCK CARD
// *******************************************************************************
// Displays stock quantities per warehouse + stock policy switches.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Empty, Flex, Typography, Tag } from 'antd';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';
import financialService from 'services/financialService';
import appService from 'services/appService';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const StockCard = ({ style, showValues = false }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const costPrice = data?.pricing?.costPrice || 0;
  const totalStock = data?.stock?.quantity || 0;
  const totalValue = financialService.roundCurrency(totalStock * costPrice);

  // Get registered warehouses from parameters
  const warehouseParams = useMemo(() => {
    const raw = appService.getModulePreferences({
      moduleId: 'products',
      category: 'warehouses',
    });
    return Array.isArray(raw) ? raw.filter(w => !w.removed) : [];
  }, [storedParameters]);

  // Build stock data: merge product.stock.warehouses with warehouse parameters
  const stockData = useMemo(() => {
    const productWarehouses = data?.stock?.warehouses || [];

    if (productWarehouses.length > 0) {
      return productWarehouses.map(pw => {
        const param = warehouseParams.find(wp => wp.uid === pw.stockUid);
        const qty = pw.quantity || 0;
        return {
          key: pw.stockUid || pw.key,
          code: param?.code || pw.code || '-',
          name: param?.name || pw.stockName || '-',
          quantity: qty,
          totalCost: financialService.roundCurrency(qty * costPrice),
          isDefault: param?.isDefault || false,
        };
      });
    }

    return warehouseParams.map(wp => ({
      key: wp.uid,
      code: wp.code || '-',
      name: wp.name,
      quantity: 0,
      totalCost: 0,
      isDefault: wp.isDefault || false,
    }));
  }, [data?.stock?.warehouses, warehouseParams, costPrice]);

  const columns = [
    {
      title: translateX('code'),
      dataIndex: 'code',
      width: 80,
      render: text => <Text strong style={{ textTransform: 'uppercase' }}>{text}</Text>,
    },
    {
      title: translateX('warehouse'),
      dataIndex: 'name',
      render: (text, record) => (
        <span>
          {text}
          {record.isDefault && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {translateX('default')}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: translateX('current_quantity'),
      dataIndex: 'quantity',
      width: 100,
      align: 'right',
      render: value => (
        <Text strong style={{ color: value > 0 ? '#52c41a' : '#8c8c8c' }}>
          {value}
        </Text>
      ),
    },
    showValues && {
      title: translateX('total_cost'),
      dataIndex: 'totalCost',
      width: 120,
      align: 'right',
      render: value => (
        <Text style={{ color: value > 0 ? undefined : '#8c8c8c' }}>
          {value > 0 ? financialService.formatCurrency(value) : '—'}
        </Text>
      ),
    },
  ].filter(Boolean);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card
      style={{ minHeight: 248, ...style }}
      title={<div className="text-muted">{translateX('stock')}</div>}
      extra={
        <Flex gap={16}>
          <Text strong>
            {translateX('total')}: {totalStock}
          </Text>
          {showValues && totalValue > 0 && (
            <Text type="secondary">
              {financialService.formatCurrency(totalValue)}
            </Text>
          )}
        </Flex>
      }
    >
      {stockData.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translateX('no_warehouses_configured')}
        />
      ) : (
        <SimpleTable
          columns={columns}
          dataSource={stockData}
          rowKey="key"
          pagination={false}
          size="small"
        />
      )}

      {/* Allow Negative Stock — inline horizontal, right-aligned */}
      <div className="pt-3">
        <SimpleSwitch
          name={['stock', 'allowNegativeStock']}
          label="allow_negative_stock"
          inline
        />
      </div>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default StockCard;
